import { useContext, useEffect, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ClerkAuthBridge() {
  const { isLoaded: authLoaded, isSignedIn, getToken, signOut } = useAuth();
  const { isLoaded: userLoaded, user: clerkUser } = useUser();
  const { user, setUser } = useContext(AuthContext);
  const syncing = useRef(false);
  const lastClerkId = useRef(null);

  useEffect(() => {
    if (!authLoaded || !userLoaded) return;

    const isSuperAdmin = user?.email?.endsWith('@superadmin.edai');

    if (isSignedIn && clerkUser && !syncing.current) {
      const needsLogin = !user || (lastClerkId.current && lastClerkId.current !== clerkUser.id);
      if (needsLogin) {
        syncing.current = true;
        (async () => {
          try {
            const token = await getToken();
            if (!token) throw new Error('Could not get Clerk token');
            const role = localStorage.getItem('pending_role') || 'student';
            const res = await axios.post(`${API}/auth/clerk/verify`, {
              token,
              is_teacher: role === 'teacher',
            }, { withCredentials: true });
            setUser(res.data);
            lastClerkId.current = clerkUser.id;
            localStorage.removeItem('pending_role');
            toast.success(`Welcome${role === 'teacher' ? ' Teacher' : ''}! 🎉`);

            // Auto-join pending competition (from share link)
            const pendingComp = localStorage.getItem('pending_competition_id');
            if (pendingComp) {
              try {
                await axios.post(`${API}/competitions/${pendingComp}/join`,
                  { join_code: null },
                  { withCredentials: true }
                );
                toast.success('Joined the competition! 🏆');
              } catch (joinErr) {
                const detail = joinErr.response?.data?.detail || '';
                // Closed competitions need a code — let user enter it on the invite page
                if (!detail.includes('Already enrolled')) {
                  // Not a hard failure — invite page will handle it
                }
              } finally {
                localStorage.removeItem('pending_competition_id');
                if (window.location.pathname.startsWith('/c/')) {
                  // Stay on invite page so user can see status / enter code
                } else {
                  window.location.href = `/c/${pendingComp}`;
                }
              }
            }
          } catch (e) {
            console.error('Clerk verify failed', e);
            toast.error(e.response?.data?.detail || 'Sign-in failed. Please try again.');
            try { await signOut(); } catch {}
          } finally {
            syncing.current = false;
          }
        })();
      }
    }

    if (!isSignedIn && user && !isSuperAdmin) {
      axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
      setUser(null);
      lastClerkId.current = null;
    }
  }, [authLoaded, userLoaded, isSignedIn, clerkUser, user, getToken, setUser, signOut]);

  return null;
}
